import {
    Button,
    Card,
    Chip,
    Collapse,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    makeStyles,
} from '@material-ui/core';
import {FC, useState} from 'react';
import Box from '@material-ui/core/Box';
import {observer} from 'mobx-react-lite';
import {useQuery} from '@tanstack/react-query';
import AthenaAPI from '@/APIs/AthenaAPI';
import RootStore from '../../Core/mobx/RootStore';
import {useHistory} from 'react-router-dom';
import {Contact} from '../types';
import {ContactInfo} from './ContactInfo';
import Utils from '../../Core/Utils';
import {LoadingSpinner} from '@/components/ui/loading-spinner';
import {FileText} from 'lucide-react';

const useStyles = makeStyles({
    row: {
        cursor: 'pointer',
        '& .MuiTableCell-root': {
            fontWeight: (props: {isOpen: boolean}) => (props.isOpen ? 'bold' : 'normal'),
        },
    },
    caption: {
        color: '#393939',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 'bold',
        lineHeight: '120%',
    },
});

const LoadingState = () => (
    <div className="flex items-center justify-center gap-3 p-4 pt-0">
        <LoadingSpinner className="w-6 h-6" />
        <span className="text-muted-foreground">Loading form records...</span>
    </div>
);

const EmptyState = () => (
    <div className="flex flex-col items-center justify-center gap-2 p-8 pt-0">
        <FileText className="h-10 w-10 text-primary-200" />
        <span className="">No form records found.</span>
    </div>
);

interface OneContactProps {
    contact: Contact;
    selectContact: ((email: string) => void) | null;
}

export const OneContact: FC<OneContactProps> = observer(({contact, selectContact}) => {
    const {agentPortalStore, assistStore} = RootStore;

    const history = useHistory();

    const [open, setOpen] = useState(false);
    const classes = useStyles({isOpen: open});

    let owner = '-';

    if (contact.owner != null && contact.owner.lastName != null) {
        owner = contact.owner?.firstName + ' ' + contact.owner?.lastName;
    }

    const {data, isLoading} = useQuery({
        queryKey: ['contact.forms', contact.id],
        queryFn: async () => {
            const resp = await AthenaAPI.getFormsForContactByEmail({email: contact.email});
            if (resp && resp?.success) return resp;
            return {
                success: false,
                data: [],
            };
        },
        enabled: open,
        keepPreviousData: true,
    });

    const onNewForm = async () => {
        assistStore.resetStore();
        await agentPortalStore.startNewForm(contact);
        history.push('/agent/newform');
    };

    const contactChipLabel = (contact: Contact) => {
        if (open) return 'Close';
        return contact.formCount;
    };

    return (
        <>
            <TableRow onClick={() => setOpen(!open)} classes={{root: classes.row}} hover>
                <TableCell>{contact.firstName}</TableCell>
                <TableCell>{contact.lastName}</TableCell>
                <TableCell>{contact.email}</TableCell>
                <TableCell>{contact.phone}</TableCell>
                <TableCell>{Utils.isoString2TimeString(contact.createdAt)}</TableCell>
                <TableCell>{owner}</TableCell>
                <TableCell>
                    <Chip label={contactChipLabel(contact)} onClick={() => setOpen(!open)} clickable color="primary" />
                </TableCell>
                {selectContact && (
                    <TableCell>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                selectContact(contact.email);
                                agentPortalStore.selectClient(contact);
                            }}
                        >
                            Select
                        </Button>
                    </TableCell>
                )}
            </TableRow>

            <TableRow>
                <TableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={7}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box
                            margin={1}
                            padding={1}
                            style={{display: 'flex', justifyContent: 'space-between', columnGap: '20px'}}
                        >
                            <Typography variant="caption" classes={{caption: classes.caption}}>
                                {`${[contact.firstName, contact.lastName].filter(Boolean).join(' ')}'s Case`}
                            </Typography>
                            <Button variant="contained" color="primary" onClick={onNewForm}>
                                + NEW CASE
                            </Button>
                        </Box>

                        {data?.data?.length ? (
                            <div className="p-4">
                                <Card>
                                    <Table style={{width: '100%'}}>
                                        <TableHead>
                                            <TableRow classes={{root: classes.row}}>
                                                <TableCell />
                                                <TableCell>Create Date</TableCell>
                                                <TableCell>Case Name</TableCell>
                                                <TableCell>Prequal</TableCell>
                                                <TableCell>Quote</TableCell>
                                                <TableCell>Preapproval</TableCell>
                                                <TableCell>UW Status</TableCell>
                                                <TableCell>UW Case Length</TableCell>
                                                <TableCell />
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data.data.map((record: any) => (
                                                <ContactInfo key={contact.id} record={record} contact={contact} />
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Card>
                            </div>
                        ) : isLoading ? (
                            <LoadingState />
                        ) : (
                            <EmptyState />
                        )}
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
});
